/**
 * HTML content utilities for safely rendering and processing HTML content
 */

// Simple HTML tag removal for basic text extraction
export const stripHtmlTags = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

// Convert HTML to safe JSX-compatible content
export const htmlToText = (html) => {
  if (!html) return '';
  
  // Replace common HTML entities
  let text = html
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Remove HTML tags but preserve line breaks
  text = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<[^>]*>/g, '');
  
  // Clean up extra whitespace
  text = text
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Multiple line breaks to double
    .replace(/^\s+|\s+$/g, '') // Trim start/end
    .replace(/[ \t]+/g, ' '); // Multiple spaces to single
  
  return text;
};

// Process HTML content while preserving links
export const processHtmlWithLinks = (html) => {
  if (!html) return null;
  
  // Replace common HTML entities first
  let processedHtml = html
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Split by links to preserve them
  const linkRegex = /<a\s+[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = linkRegex.exec(processedHtml)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      const beforeText = processedHtml.substring(lastIndex, match.index);
      const cleanText = htmlToText(beforeText);
      if (cleanText.trim()) {
        parts.push(cleanText);
      }
    }
    
    // Add the link as a JSX element
    const href = match[1];
    const linkText = match[2];
    parts.push(
      <a key={match.index} href={href} target="_blank" rel="noopener noreferrer">
        {linkText}
      </a>
    );
    
    lastIndex = linkRegex.lastIndex;
  }
  
  // Add remaining text after the last link
  if (lastIndex < processedHtml.length) {
    const remainingText = processedHtml.substring(lastIndex);
    const cleanText = htmlToText(remainingText);
    if (cleanText.trim()) {
      parts.push(cleanText);
    }
  }
  
  // If no links were found, fall back to regular text processing
  if (parts.length === 0) {
    return textToJSX(htmlToText(processedHtml));
  }
  
  return parts.length === 1 ? parts[0] : parts;
};

// Convert text with line breaks to JSX elements
export const textToJSX = (text) => {
  if (!text) return null;
  
  const lines = text.split('\n');
  return lines.map((line, index) => (
    <span key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </span>
  ));
};

// Process HTML content for safe display
export const processHtmlContent = (html) => {
  const cleanText = htmlToText(html);
  return textToJSX(cleanText);
};

// Check if content contains HTML tags
export const containsHtml = (content) => {
  if (!content) return false;
  return /<[^>]*>/g.test(content);
};

// Safe content renderer - automatically detects and processes HTML
export const SafeContent = ({ content, className = '', preserveLinks = false }) => {
  if (!content) return null;
  
  if (containsHtml(content)) {
    return (
      <div className={className}>
        {preserveLinks ? processHtmlWithLinks(content) : processHtmlContent(content)}
      </div>
    );
  }
  
  return (
    <div className={className}>
      {textToJSX(content)}
    </div>
  );
};

export default {
  stripHtmlTags,
  htmlToText,
  textToJSX,
  processHtmlContent,
  processHtmlWithLinks,
  containsHtml,
  SafeContent
};